import type { MediaItem, ReactionSummary, Granularity } from '../types';

export function getGroupKey(timestamp: number, granularity: Granularity): string {
  const d = new Date(timestamp);
  switch (granularity) {
    case 'year':
      return `${d.getFullYear()}`;
    case 'month':
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    case 'day':
      return d.toDateString();
  }
}

export function formatGroupHeader(key: string, granularity: Granularity): string {
  switch (granularity) {
    case 'year':
      return key;
    case 'month': {
      const [y, m] = key.split('-');
      return new Date(+y!, +m! - 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    }
    case 'day': {
      const date = new Date(key);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return 'Today';
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return date.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }
  }
}

export function engagementScore(item: MediaItem): number {
  const reactionTotal = item.reactions?.reduce((sum: number, r: ReactionSummary) => sum + r.count, 0) ?? 0;
  return reactionTotal + item.commentCount;
}

const FEATURED_EVERY = 7;
const MIN_FEATURED_GAP = 5;

export function getFeaturedIds(items: MediaItem[]): Set<string> {
  const featured = new Set<string>();
  const featuredIndices: number[] = [];
  if (items.length === 0) return featured;

  featured.add(items[0]!.id);
  featuredIndices.push(0);

  const remaining = items.length - 1;
  if (remaining > 0) {
    const numSlots = Math.ceil(remaining / FEATURED_EVERY);
    for (let slot = 0; slot < numSlots; slot++) {
      const start = 1 + slot * FEATURED_EVERY;
      const end = Math.min(start + FEATURED_EVERY, items.length);
      let bestIdx = -1;
      let bestScore = -1;
      for (let i = start; i < end; i++) {
        if (featuredIndices.some(fi => Math.abs(i - fi) < MIN_FEATURED_GAP)) continue;
        const score = engagementScore(items[i]!);
        if (score > bestScore) {
          bestScore = score;
          bestIdx = i;
        }
      }
      if (bestIdx !== -1) {
        featured.add(items[bestIdx]!.id);
        featuredIndices.push(bestIdx);
      }
    }
  }
  return featured;
}

export function groupMedia(items: MediaItem[], granularity: Granularity): Map<string, MediaItem[]> {
  const groups = new Map<string, MediaItem[]>();
  for (const item of items) {
    const key = getGroupKey(item.takenAt || item.createdAt, granularity);
    const existing = groups.get(key) || [];
    existing.push(item);
    groups.set(key, existing);
  }
  return groups;
}
